import { DT } from '../../theme/designTokens';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useTranslation } from '../../i18n/react';

/**
 * OfflineBanner — the ONE global "you've lost your connection" surface,
 * owned by AppShell (same ownership pattern as ActiveGameBanner). Shown on
 * every authenticated-shell screen EXCEPT /game and /spectate, which already
 * manage their own connection UX for the live ActionCable session (see
 * useOnlineStatus.ts) — this covers everywhere else (Friends, Lobby, Home,
 * Messages, Settings, ...) where a dropped connection previously failed
 * silently: API calls would just error with no explanation of why.
 *
 * A thin top ribbon rather than a floating card (unlike ActiveGameBanner):
 * this is a passive status indicator, not an action prompt, and sits above
 * TopNav/Sidebar so it stays visible regardless of which shell variant is
 * active. Auto-hides the instant the browser/WebView reports 'online' —
 * no dismiss button, nothing to persist.
 */
export function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const { t } = useTranslation('common');

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position:      'fixed',
        top:           0,
        left:          0,
        right:         0,
        zIndex:        DT.zIndex.toast + 1, // above TopNav/Sidebar (nav) and the floating ActiveGameBanner alike
        paddingTop:    DT.layout.safeAreaTop,
        background:    DT.color.teamRed,
        boxShadow:     '0 2px 8px rgba(0,0,0,0.35)',
      }}
    >
      <div
        style={{
          display:       'flex',
          alignItems:    'center',
          justifyContent: 'center',
          gap:           8,
          padding:       '8px 16px',
          textAlign:     'center',
        }}
      >
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: '50%', background: '#ffffff', flexShrink: 0 }}
        />
        <span style={{ fontFamily: DT.font.sans, fontWeight: 700, fontSize: 13, color: '#ffffff' }}>
          {t('connectivity.offline')}
        </span>
        <span
          style={{
            fontFamily: DT.font.sans, fontSize: 12, color: 'rgba(255,255,255,0.85)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {t('connectivity.offlineDetail')}
        </span>
      </div>
    </div>
  );
}
